import { Injectable, inject } from '@angular/core';
import { CmsApiService } from './api/cms-api.service';
import { UserService } from './user/user.service';
import { TimeService } from './time.service';
import { LocalstorageService } from './localstorage.service';
import { SsApiService } from './api/ss-api.service';
import { WindowService } from './window.service';
import { Payment, PaymentsMethod } from '../vendor/payment-v2/ss-payment-types';
import { UserPhoneService } from './user/user-phone.service';
import { ScriptLoaderService } from 'ngx-unificator/services';
import {firstValueFrom, from, lastValueFrom} from 'rxjs';
import { EXCLUDE_SEON_TEST_GROUP, FIRST_CASHOUT, FIRST_DEP, SECOND_DEP } from './user/data/user-group.data';
import { UserGroupsService } from './user/user-groups.service';
import {filter, first, switchMap, tap} from "rxjs/operators";
import {BrandConfigService} from "./brand-config/brand-config.service";

export const LOCAL_STORAGE_FRAUD_KEY_DEPOSIT = 'deposit';
export const LOCAL_STORAGE_FRAUD_KEY_CASHOUT = 'cashout';
export const BRAND_ID = '21Bit';

export const FIRST_TIME_DEPOSIT_ACTION = 'first_time_deposit';
export const SECOND_TIME_DEPOSIT_ACTION = 'second_time_deposit';
export const FIRST_TIME_CASHOUT_ACTION = 'first_time_cashout';

declare let seon: any;

@Injectable({
  providedIn: 'root',
})
export class FraudCheckService {
  private _api = inject(CmsApiService);
  private _user = inject(UserService);
  private _time = inject(TimeService);
  private _storage = inject(LocalstorageService);
  private _ssApi = inject(SsApiService);
  private _window = inject(WindowService);
  private _phone = inject(UserPhoneService);
  private _scriptLoader = inject(ScriptLoaderService);
  private _groups = inject(UserGroupsService);
  private _brandConfig = inject(BrandConfigService);

  private _currentIp: string;
  private _session: string;

  constructor() {
  }

  /**
   * Check SEON fraud user data
   */
  public postFraudData(dataStr: string) {
    return this._api.postBonusCheck(dataStr, { f: 'b' });
  }

  /**
   * Set cashout data string
   * @param method
   * @param formData
   * @param savedAccount
   */
  public setCashoutUserData(
    method: PaymentsMethod,
    formData: any,
    savedAccount: any,
  ) {

    this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_CASHOUT);

    if (!this._groups.isExistGroup(EXCLUDE_SEON_TEST_GROUP)) {
      if (!this._groups.isExistGroup(FIRST_CASHOUT)) {
        this._handleCashoutFields(
          method,
          formData,
          FIRST_TIME_CASHOUT_ACTION,
          savedAccount,
        );
      }
    }
  }

  /**
   * Set deposit data string
   * @param method
   * @param formData
   * @param savedAccount
   */
  public setDepositUserData(
    method: PaymentsMethod,
    formData: any,
    savedAccount: any) {

    this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);

    if (!this._groups.isExistGroup(EXCLUDE_SEON_TEST_GROUP)) {
      if ((!this._groups.isExistGroup(FIRST_DEP) &&
        !this._groups.isExistGroup(SECOND_DEP))) {
        this._handleDepositFields(
          method,
          formData,
          FIRST_TIME_DEPOSIT_ACTION,
          savedAccount,
        );
      }

      if ((this._groups.isExistGroup(FIRST_DEP) &&
        !this._groups.isExistGroup(SECOND_DEP))) {
        this._handleDepositFields(
          method,
          formData,
          SECOND_TIME_DEPOSIT_ACTION,
          savedAccount,
        );
      }
    }
  }

  /**
   * Check if body length
   */
  public isBodyFraudStorageLength(dataType: string): boolean {
    return Boolean(this._storage.get(dataType) &&
      this._storage.get(dataType).length);
  }

  /**
   * Init additional fields
   * @public
   */
  public async initFraudCheck() {
    this._user.auth$.pipe(
      filter(Boolean),
      switchMap(() => this._brandConfig.brandConfigResolved$),
      filter(Boolean),
      first(),
      switchMap(() => this._ssApi.currentIp()),
      switchMap(({ ip }) =>
        from(this._getSeonFingerprintSession()).pipe(
          tap(session => {
            this._currentIp = ip;
            this._session = session;
          })
        )
      )
    ).subscribe();
  }

  /**
   * Init additional fields
   * @private
   */
  private async _initAdditionalFields() {
    try {
      const { ip } = await firstValueFrom(this._ssApi.currentIp());
      const session = await this._getSeonFingerprintSession();
      this._currentIp = ip;
      this._session = session;
    } catch (e) {
      throw new Error(e);
    }
  }

  /**
   * Handle deposit fields
   * @param method
   * @param formData
   * @param actionType
   * @param savedAccount
   * @private
   */
  private _handleDepositFields(
    method: PaymentsMethod,
    formData: any,
    actionType: string,
    savedAccount: any,
  ) {
    const body = {
      ...this._mainSetupFields(formData, actionType, method),
      ...(method.type === Payment.MethodType.Cards ?
        this._resolveCardData(formData, savedAccount) : {}),
      userFullname: `${formData?.first_name ||
      this._user.info?.first_name} ${formData?.last_name ||
      this._user.info?.last_name}`,
      userName: formData?.first_name || this._user.info?.first_name,
      userDob: this._convertDobFormat(formData?.date_of_birth) ||
        this._user.info?.date_of_birth,
      userCity: formData?.city || this._user.info?.city,
      userZip: formData?.postal_code || this._user.info?.postal_code,
      userStreet: formData?.address || this._user.info?.address,
      userStreet2: formData?.address || this._user.info?.address,
      phoneNumber: formData?.mobile_phone ?
        `${this._phone.state().phonePrefix}${formData?.mobile_phone}`
          ?.replace(/[\s+]/g, '') :
        this._user.info.mobile_phone?.replace(/[\s+]/g, ''),
    };
    this._storage.set(
      LOCAL_STORAGE_FRAUD_KEY_DEPOSIT,
      this._encodeToHex(this._encodeToBase64(JSON.stringify(body))));
  }

  /**
   * Handle cashout fields
   * @param method
   * @param formData
   * @param actionType
   * @param savedAccount
   * @private
   */
  private _handleCashoutFields(
    method: PaymentsMethod,
    formData: any,
    actionType: string,
    savedAccount: any,
  ) {
    const body = {
      ...this._mainSetupFields(formData, actionType, method),
      userFullname: `${formData?.first_name ||
      this._user.info?.first_name} ${formData?.last_name ||
      this._user.info?.last_name}`,
      userName: formData?.first_name || this._user.info?.first_name,
      userDob: this._convertDobFormat(formData?.date_of_birth) ||
        this._user.info?.date_of_birth,
      userCity: formData?.city || this._user.info?.city,
      userZip: formData?.postal_code || this._user.info?.postal_code,
      userStreet: formData?.address || this._user.info?.address,
      userStreet2: formData?.address || this._user.info?.address,
      phoneNumber: formData?.mobile_phone ?
        `${this._phone.state().phonePrefix}${formData?.mobile_phone}`
          ?.replace(/[\s+]/g, '') :
        this._user.info?.mobile_phone?.replace(/[\s+]/g, ''),
      userBankAccount: formData?.accountNumber || formData?.iban,
      userBankName: formData?.bankName,
      cardFullname: savedAccount?.cardHolder,
      cardBin: savedAccount?.title?.substring(0, 6),
      cardExpire: savedAccount?.expiryDate,
      cardLast: savedAccount?.title?.substring(savedAccount?.title.length - 4),
    };
    this._storage.set(
      LOCAL_STORAGE_FRAUD_KEY_CASHOUT,
      this._encodeToHex(this._encodeToBase64(JSON.stringify(body))));
  }

  /**
   * Get seon fingerprint
   * @private
   */
  private async _getSeonFingerprintSession() {
    await this._loadFingerprintAgent();
    seon = this._window.nativeWindow['seon'] || {};
    seon.config({
      session_id: `${BRAND_ID}-${Math.random().toString(36).substring(2, 9)}`,
      audio_fingerprint: true,
      canvas_fingerprint: true,
      webgl_fingerprint: true,
      silent_mode: false,
      max_proxy_delay: 1000,
    });
    return await seon.getBase64Session();
  }

  /**
   * Main seon fields
   * @param formData
   * @param actionType
   * @param session
   * @param method
   * @private
   */
  private _mainSetupFields(formData: any, actionType: string, method: PaymentsMethod) {
    return {
      session: this._session,
      ip: this._currentIp,
      actionType,
      affiliateId: this._storage.get('stag') ? this._storage.get('stag')?.split('_')[0] : null,
      email: this._user.info?.email,
      emailDomain: this._getEmailDomain(this._user.info?.email),
      userId: `${BRAND_ID}-${this._user.info?.id}`,
      userVerificationLevel: this._user.info?.verified ? 'ID_verified' : null,
      userCreated: this._time.dateToTimestamp(this._user.info.created_at),
      userCountry: this._user.info.country,
      userBalance: this._user.currentCurrency.amount,
      regulation: 'Curacao',
      paymentProvider: method?.provider,
      paymentMode: method?.brand,
      transactionAmount: formData && formData?.amount,
      transactionCurrency: method?.currency,
      brandId: BRAND_ID,
    };
  }

  /**
   * Resolves the card data based on the form data and saved account information.
   * If a saved account is provided, it uses the card data from the saved account.
   * Otherwise, it extracts the card data from the form data.
   * @param formData - The form data containing the card information.
   * @param savedAccount - The saved account information, if available.
   * @returns An object containing the resolved card data.
   */
  private _resolveCardData(formData: any, savedAccount: any) {
    return {
      cardFullname: !savedAccount ? formData?.cardHolder || formData?.holder || null : savedAccount?.cardHolder,
      cardBin: !savedAccount ?
        formData?.encCreditcardNumber?.substring(0, 6) || formData?.number?.substring(0, 6) || null :
        savedAccount?.title?.substring(0, 6),
      cardExpire: !savedAccount ?
        formData?.expiry_date?.replace('/', '-') || formData?.seamless_expiry_date?.replace('/', '-') || null :
        savedAccount?.expiryDate,
      cardLast: !savedAccount ?
        formData?.encCreditcardNumber?.substring(formData?.encCreditcardNumber?.length - 4) || formData?.number?.substring(formData?.number?.length - 4) || null :
        savedAccount?.title?.substring(savedAccount?.title?.length - 4),
    };
  }

  /**
   * Load fingerprint agent
   * @private
   */
  private async _loadFingerprintAgent() {
    return await lastValueFrom(
      this._scriptLoader.load('https://cdn.seondf.com/js/v5/agent.js'),
    );
  }

  /**
   * Substring email domain
   * sverevkin25@gmail.com => gmail.com
   * @param email
   * @private
   */
  private _getEmailDomain(email: string) {
    return email.substring(email.indexOf('@') + 1);
  }

  private _convertDobFormat(date: string) {
    if (date) {
      const parts = date.split('/');
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
  }

  /**
   * Get user id from decoded string
   */
  public getUserIdFromDecodedString(): number {
    const { userId } = JSON.parse
    (
      this._decodeFromBase64(
        this._decodeFromHex(this._storage.get(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT)),
      ),
    );
    return Number(userId?.split('-')[1]);
  }

  /**
   * Encode object string to hex string
   * example {regulation: "Curacao"} => 772828137213
   * @param jsonString
   * @private
   */
  private _encodeToHex(jsonString: string) {
    let result = '';
    for (let i = 0; i < jsonString.length; i++) {
      result += jsonString.charCodeAt(i).toString(16);
    }
    return result;
  }

  private _decodeFromHex(hexString: string) {
    let result = '';
    for (let i = 0; i < hexString.length; i += 2) {
      result += String.fromCharCode(parseInt(hexString.substr(i, 2), 16));
    }
    return result;
  }

  private _decodeFromBase64(base64String: string) {
    return decodeURIComponent(escape(atob(base64String)));
  }

  private _encodeToBase64(jsonString: string) {
    return btoa(unescape(encodeURIComponent(jsonString)));
  }

}
